import { Capacitor } from '@capacitor/core';
import { AdMob } from '@capacitor-community/admob';
import { appendMonetizationEvent } from './monetizationEvents';

export const AD_CAP_STORAGE_KEY = 'ad_cap_state_v1';
export type AdPlacement = 'insights_open' | 'export';

type AdPlacementPolicy = {
  minIntervalMs: number;
  maxPerDay: number;
};

export const AD_PLACEMENT_POLICY: Record<AdPlacement, AdPlacementPolicy> = {
  insights_open: {
    minIntervalMs: 12 * 60 * 1000,
    maxPerDay: 3
  },
  export: {
    minIntervalMs: 12 * 60 * 1000,
    maxPerDay: 3
  }
};

type AdCapState = {
  day: string;
  counts: Partial<Record<AdPlacement, number>>;
  lastShownAtMsByPlacement: Partial<Record<AdPlacement, number>>;
};

type MaybeShowInterstitialParams = {
  enabled: boolean;
  personalized: boolean;
  placement: AdPlacement;
};

export async function maybeShowInterstitialAd({ enabled, personalized, placement }: MaybeShowInterstitialParams): Promise<boolean> {
  if (!enabled) {
    appendMonetizationEvent({ category: 'ads', action: `interstitial_${placement}`, status: 'info', detail: 'Ads disabled for current tier or settings.' });
    return false;
  }
  if (!Capacitor.isNativePlatform()) {
    appendMonetizationEvent({ category: 'ads', action: `interstitial_${placement}`, status: 'info', detail: 'Skipped because app is not running on a native platform.' });
    return false;
  }
  if (!isEligibleForInterstitial(placement)) {
    appendMonetizationEvent({ category: 'ads', action: `interstitial_${placement}`, status: 'info', detail: 'Suppressed by local placement cap.' });
    return false;
  }

  const adId = interstitialAdUnitId();
  if (!adId) {
    appendMonetizationEvent({ category: 'ads', action: `interstitial_${placement}`, status: 'error', detail: 'Interstitial unit ID is missing.' });
    return false;
  }

  try {
    await AdMob.initialize({
      initializeForTesting: import.meta.env.DEV
    });
    await AdMob.prepareInterstitial({
      adId,
      isTesting: import.meta.env.DEV,
      npa: !personalized
    });
    await AdMob.showInterstitial();
    markInterstitialShown(placement);
    appendMonetizationEvent({
      category: 'ads',
      action: `interstitial_${placement}`,
      status: 'success',
      detail: personalized ? 'Interstitial shown with personalized ads allowed.' : 'Interstitial shown with non-personalized ads.'
    });
    return true;
  } catch {
    appendMonetizationEvent({ category: 'ads', action: `interstitial_${placement}`, status: 'error', detail: 'Interstitial load or show failed.' });
    return false;
  }
}

function interstitialAdUnitId(): string | null {
  if (Capacitor.getPlatform() !== 'android') return null;
  const value = import.meta.env.VITE_ADMOB_ANDROID_INTERSTITIAL_ID;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isEligibleForInterstitial(placement: AdPlacement): boolean {
  const state = readCapState();
  const policy = AD_PLACEMENT_POLICY[placement];
  const now = Date.now();
  const today = isoDay(now);
  if (state.day !== today) return true;
  const count = state.counts[placement] ?? 0;
  if (count >= policy.maxPerDay) return false;
  const lastShownAtMs = state.lastShownAtMsByPlacement[placement] ?? 0;
  return now - lastShownAtMs >= policy.minIntervalMs;
}

function markInterstitialShown(placement: AdPlacement): void {
  const now = Date.now();
  const today = isoDay(now);
  const state = readCapState();
  const next: AdCapState =
    state.day === today
      ? {
          day: today,
          counts: {
            ...state.counts,
            [placement]: (state.counts[placement] ?? 0) + 1
          },
          lastShownAtMsByPlacement: {
            ...state.lastShownAtMsByPlacement,
            [placement]: now
          }
        }
      : {
          day: today,
          counts: {
            [placement]: 1
          },
          lastShownAtMsByPlacement: {
            [placement]: now
          }
        };
  localStorage.setItem(AD_CAP_STORAGE_KEY, JSON.stringify(next));
}

function readCapState(): AdCapState {
  const fallback: AdCapState = { day: isoDay(Date.now()), counts: {}, lastShownAtMsByPlacement: {} };
  const raw = localStorage.getItem(AD_CAP_STORAGE_KEY);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as Partial<AdCapState>;
    if (typeof parsed.day !== 'string') return fallback;
    return {
      day: parsed.day,
      counts: normalizePlacementNumberMap(parsed.counts),
      lastShownAtMsByPlacement: normalizePlacementNumberMap(parsed.lastShownAtMsByPlacement)
    };
  } catch {
    return fallback;
  }
}

function isoDay(tsMs: number): string {
  return new Date(tsMs).toISOString().slice(0, 10);
}

function normalizePlacementNumberMap(value: unknown): Partial<Record<AdPlacement, number>> {
  if (typeof value !== 'object' || value === null) return {};

  const result: Partial<Record<AdPlacement, number>> = {};
  for (const placement of Object.keys(AD_PLACEMENT_POLICY) as AdPlacement[]) {
    const raw = (value as Record<string, unknown>)[placement];
    const parsed = Number(raw ?? 0);
    if (!Number.isFinite(parsed)) continue;
    result[placement] = Math.max(0, Math.floor(parsed));
  }
  return result;
}
